var __importDefault = (this && this.__importDefault) || function (mod) {
  return (mod && mod.__esModule) ? mod : { "default": mod };
};
/**
 * MonthPicker Component
 * Renders a 12-month grid for year overview/selection
 */
import React, { memo, useCallback, useMemo } from 'react';
import { format } from 'date-fns';
import { styles } from './MonthPicker.styles';
const MONTHS_PER_ROW = 4;
const MONTH_NAMES = [
    'Jan', 'Feb', 'Mar', 'Apr',
    'May', 'Jun', 'Jul', 'Aug',
    'Sep', 'Oct', 'Nov', 'Dec'
];
/**
 * MonthPicker - 12-month grid component
 */
export const MonthPicker = memo(({ activeDate, selectedDate, config, onMonthSelect, minDate, maxDate, year, }) => {
    const displayYear = year !== null && year !== void 0 ? year : activeDate.getFullYear();
    const currentMonth = new Date().getMonth();
    const currentYear = new Date().getFullYear();
    const selectedMonth = selectedDate === null || selectedDate === void 0 ? void 0 : selectedDate.getMonth();
    const selectedYear = selectedDate === null || selectedDate === void 0 ? void 0 : selectedDate.getFullYear();
    // Check if a month is disabled
    const isMonthDisabled = useCallback((month) => {
        if (!minDate && !maxDate)
            return false;
        const monthStart = new Date(displayYear, month, 1);
        const monthEnd = new Date(displayYear, month + 1, 0);
        if (minDate && monthEnd < minDate)
            return true;
        if (maxDate && monthStart > maxDate)
            return true;
        return false;
    }, [displayYear, minDate, maxDate]);
    // Handle month click
    const handleMonthClick = useCallback((month) => {
        if (!isMonthDisabled(month)) {
            onMonthSelect === null || onMonthSelect === void 0 ? void 0 : onMonthSelect(month, displayYear);
        }
    }, [displayYear, isMonthDisabled, onMonthSelect]);
    // Generate month grid
    const monthGrid = useMemo(() => {
        const rows = [];
        for (let i = 0; i < 12; i += MONTHS_PER_ROW) {
            rows.push(Array.from({ length: MONTHS_PER_ROW }, (_, j) => i + j));
        }
        return rows;
    }, []);
    // Render a month cell
    const renderMonth = useCallback((month) => {
        const isDisabled = isMonthDisabled(month);
        const isCurrent = month === currentMonth && displayYear === currentYear;
        const isSelected = month === selectedMonth && displayYear === selectedYear;
        const cellStyle = Object.assign(Object.assign(Object.assign(Object.assign({}, styles.monthCell), (isDisabled ? styles.monthDisabled : {})), (isCurrent ? styles.monthCurrent : {})), (isSelected ? styles.monthSelected : {}));
        return (React.createElement("button", { key: month, style: cellStyle, onClick: () => handleMonthClick(month), disabled: isDisabled, "aria-label": format(new Date(displayYear, month), 'MMMM yyyy'), "aria-selected": isSelected, "aria-current": isCurrent ? 'date' : undefined, tabIndex: isDisabled ? -1 : 0 }, MONTH_NAMES[month]));
    }, [displayYear, currentMonth, currentYear, selectedMonth, selectedYear, isMonthDisabled, handleMonthClick]);
    return (React.createElement("div", { style: styles.container, role: "grid", "aria-label": `${displayYear} months` },
        React.createElement("div", { style: styles.yearLabel }, displayYear),
        React.createElement("div", { style: styles.grid }, monthGrid.map((row, rowIndex) => (React.createElement("div", { key: rowIndex, style: styles.row, role: "row" }, row.map(month => renderMonth(month))))))));
});
MonthPicker.displayName = 'MonthPicker';
export default MonthPicker;
//# sourceMappingURL=MonthPicker.js.map